import type { DashboardActivity } from "@repo/shared/types";
import { Activity, CheckCircle, FileText, PenLine, Send, UserPlus } from "lucide-react";
import { Link } from "@/components/ui/link";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { useDashboardFormat } from "../use-dashboard-format";
import { T } from "./recent-activity-widget.i18n";
import { TONE_TINT } from "./tone";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

/** Activity type → icon and tint (see the add-activity-feed-event skill). */
const activityConfig = {
  flow_submitted: { icon: Send, tint: TONE_TINT.info },
  flow_completed: { icon: CheckCircle, tint: TONE_TINT.success },
  client_created: { icon: UserPlus, tint: TONE_TINT.info },
  signature_signed: { icon: PenLine, tint: TONE_TINT.brand },
  pdf_generated: { icon: FileText, tint: TONE_TINT.warning },
} satisfies Record<DashboardActivity["type"], unknown>;

/** Today's recent activity, unchanged. */
export function RecentActivityWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("recent_activity", target);
  const t = useT(T);
  const { relative } = useDashboardFormat();

  return (
    <WidgetCard
      icon={Activity}
      title={t.recentActivity}
      description={t.latestEvents}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.length === 0 ? (
          <WidgetEmpty>{t.noRecentActivity}</WidgetEmpty>
        ) : (
          <div className="space-y-2">
            {data.map((activity) => (
              <ActivityRow
                key={activity.id}
                activity={activity}
                when={relative(activity.timestamp)}
              />
            ))}
          </div>
        ))}
    </WidgetCard>
  );
}

/** One feed entry; the whole row links to the flow or client when there is one. */
function ActivityRow({ activity, when }: { activity: DashboardActivity; when: string }) {
  const config = activityConfig[activity.type];
  const Icon = config.icon;
  const content = (
    <div className="flex items-start gap-3">
      <div className={cn("rounded-full p-2", config.tint)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{activity.title}</p>
        <p className="truncate text-sm text-muted-foreground">{activity.description}</p>
        <p className="mt-1 text-xs text-muted-foreground">{when}</p>
      </div>
    </div>
  );
  return activity.link ? (
    <Link
      to={activity.link}
      className="-mx-2 block rounded-lg px-2 py-1 transition-colors hover:bg-accent/50"
    >
      {content}
    </Link>
  ) : (
    <div className="-mx-2 px-2 py-1">{content}</div>
  );
}
